import React, { useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { MessageCircle, ChevronDown } from "lucide-react";
import { useScrollReveal } from "../hooks/useScrollReveal";

const faqSections = [
  {
    title: "General Information",
    items: [
      {
        question: "What services does Domigo offer?",
        answer:
          "Domigo provides professional pet care services including boarding, daycare, grooming, and personalized care to ensure your pet's comfort, safety, and well-being.",
      },
      {
        question: "Where is Domigo located?",
        answer:
          "Domigo is currently located in Indore, Madhya Pradesh, at: Indore Bypass Road, near Kushabhau Thackrey Road, opposite Bharat Benz Showroom, Indore – 452016.",
      },
      {
        question: "Are you planning to expand to other cities?",
        answer:
          "Yes, Domigo is designed as a scalable pet hospitality platform, and we plan to expand to multiple cities in the future.",
      },
    ],
  },
  {
    title: "Booking & Onboarding",
    items: [
      {
        question: "How can I book a service?",
        answer:
          "Bookings can be made by completing our onboarding form and selecting the desired service and dates. Our team will assist you through the process if needed.",
      },
      {
        question: "Is onboarding mandatory?",
        answer:
          "Yes, onboarding is a one-time process where we collect essential information about your pet to ensure safe and personalized care.",
      },
      {
        question: "Can I visit the facility before booking?",
        answer:
          "Yes, facility visits can be arranged. We recommend scheduling an appointment in advance for a guided visit.",
      },
    ],
  },
  {
    title: "Health & Safety",
    items: [
      {
        question: "Are vaccinations required for boarding?",
        answer:
          "Yes, all pets must be up-to-date with their vaccinations. This is mandatory to maintain a safe environment for all pets.",
      },
      {
        question: "How are pets with special medical needs handled?",
        answer:
          "All medical conditions must be disclosed during onboarding. Our team will review the requirements and advise accordingly.",
      },
      {
        question: "What measures are in place for emergencies?",
        answer:
          "Domigo has trained staff and access to veterinary support to handle emergencies efficiently and responsibly.",
      },
    ],
  },
  {
    title: "Stay & Care",
    items: [
      {
        question: "Will my pet be kept in a kennel at all times?",
        answer:
          "No, pets are provided with comfortable kennel spaces for rest, along with supervised playtime and regular activity sessions.",
      },
      {
        question: "What is a typical daily routine for pets?",
        answer:
          "Pets follow a structured routine that includes feeding, walks, playtime, rest periods, and monitored social interaction.",
      },
      {
        question: "Can I provide my pet's food or personal items?",
        answer:
          "Yes, you may provide your pet's food, bedding, or toys to ensure familiarity and comfort during their stay.",
      },
    ],
  },
  {
    title: "Communication & Updates",
    items: [
      {
        question: "Will I receive updates about my pet?",
        answer:
          "Yes, regular updates including photos and videos are shared via WhatsApp to keep you informed.",
      },
      {
        question: "Can I contact the facility during my pet's stay?",
        answer:
          "Yes, our team is available during working hours for any queries or updates.",
      },
    ],
  },
  {
    title: "Payments & Policies",
    items: [
      {
        question: "What payment methods are accepted?",
        answer:
          "We accept UPI, online payments, and other standard digital payment methods.",
      },
      {
        question: "What is your cancellation policy?",
        answer:
          "Cancellation policies may vary depending on the service and booking. Details will be shared at the time of booking.",
      },
    ],
  },
  {
    title: "Additional Services",
    items: [
      {
        question: "Do you offer grooming services?",
        answer:
          "Yes, grooming services are available and can be booked independently or along with other services.",
      },
      {
        question: "Is daycare available?",
        answer:
          "Yes, daycare services are available for short-duration stays during the day.",
      },
    ],
  },
];

const FaqPage = () => {
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const headerReveal = useScrollReveal({ threshold: 0.1 });

  return (
    <div className="min-h-screen visitor-page">
      <Navbar />

      <section className="bg-gradient-to-br from-white via-domigo-pink/10 to-domigo-purple/5 py-16 lg:py-24">
        <div
          ref={headerReveal.ref}
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center transition-all duration-1000 ${headerReveal.isVisible
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 translate-y-12'
            }`}
        >
          <div className="inline-flex items-center justify-center mb-6">
            <div className="p-4 bg-gradient-pink rounded-3xl shadow-pink">
              <MessageCircle className="text-white" size={48} />
            </div>
          </div>
          <h1 className="text-4xl lg:text-6xl font-bold bg-gradient-to-r from-gray-900 via-domigo-pink to-domigo-purple bg-clip-text text-transparent leading-tight mb-6">
            Frequently Asked Questions
          </h1>
          <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Answers to common questions about Domigo services, onboarding,
            safety, and daily pet care.
          </p>
        </div>
      </section>

      <section className="bg-gradient-to-b from-white to-gray-50 py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold bg-gradient-to-r from-gray-900 via-domigo-pink to-domigo-purple bg-clip-text text-transparent mb-4">
              Find Answers By Topic
            </h2>
            <p className="text-lg text-gray-600">
              Browse each section to quickly find what you need.
            </p>
          </div>

          <div className="space-y-10">
            {faqSections.map((section, sectionIndex) => (
              <div key={section.title}>
                <h3 className="text-2xl font-bold text-gray-900 mb-5">
                  {section.title}
                </h3>
                <div className="space-y-4">
                  {section.items.map((faq, itemIndex) => {
                    const faqIndex = `${sectionIndex}-${itemIndex}`;
                    const isOpen = openFaqIndex === faqIndex;

                    return (
                      <div
                        key={faq.question}
                        className="bg-white rounded-3xl shadow-soft hover:shadow-medium transition-all duration-300"
                      >
                        <button
                          onClick={() =>
                            setOpenFaqIndex(isOpen ? null : faqIndex)
                          }
                          className="w-full p-6 flex items-center justify-between text-left"
                        >
                          <h4 className="text-lg font-bold text-gray-900 pr-4">
                            {faq.question}
                          </h4>
                          <ChevronDown
                            size={24}
                            className={`text-domigo-pink flex-shrink-0 transition-transform duration-300 ${isOpen ? "transform rotate-180" : ""
                              }`}
                          />
                        </button>
                        {isOpen && (
                          <div className="px-6 pb-6 animate-fade-in">
                            <p className="text-gray-600 leading-relaxed border-t border-gray-100 pt-4">
                              {faq.answer}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-14 bg-white rounded-3xl p-8 shadow-soft">
            <h3 className="text-2xl font-bold text-gray-900 mb-3">Need More Help?</h3>
            <p className="text-gray-600 leading-relaxed">
              If you have any additional questions or require assistance, please
              feel free to contact us via WhatsApp or submit an enquiry through
              our website. Our team will be happy to assist you.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FaqPage;